import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Progress,
  Row,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import UploadImageSingle from "components/Common/UploadImageSingle";
import { UploadProps } from "antd";
import { getAgencyDetail, postAgency, putAgency } from "store/agency/thunk";
import { formatDateStringToKorea } from "helpers/format";
import { unique } from "helpers/validate_yup";
import DropdownCountries from "components/Common/DropdownCountries";
import DropdownSNS from "components/Common/DropdownSNS";


export interface Props {
  id?: string;
  triggerRefresh?: () => void;
  isShowDetail?: boolean;
  setLinkImage?: any;
  setIsOpen?: any;
  setItemToEdit?: any;
}

registerLocale("en", en);
registerLocale("ko", ko);

Yup.addMethod(Yup.array, "unique", unique);

const uploadProps: UploadProps = {
  name: "files",
  accept: "image/png,image/jpeg,image/*",
  beforeUpload: () => false,
  headers: {
    authorization: "authorization-text",
  },
};

const AgencyForm = ({
  id = "",
  triggerRefresh,
  isShowDetail = false,
  setLinkImage,
  setIsOpen,
  setItemToEdit,
}: Props) => {
  const { t, i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFirstProgress, setIsFirstProgress] = useState<boolean>(true);
  
  const handleSubmit = async (values: any) => {
    try {
      setIsLoading((_prev) => true);

      const data = {
        name: values?.name,
        contact_name: values?.contact_name,
        status: values?.status === 1 ? 1 : 0,
        nationality: values?.nationality ?? 'KR',
        sns_type: values?.sns_type ?? '',
        sns_url: values?.sns_url ?? '',
        contact_phone: values?.contact_phone ?? '',
        email: values?.email ?? '',
        avatar: values?.avatar
        // ...(values?.email && {email: values?.email}),
        // ...(values?.avatar && {avatar: values?.avatar}),
        // ...(values?.contact_phone && {contact_phone: values?.contact_phone}),
      };

      const response: any = id ? await putAgency(id, data) : await postAgency(data);

      if (response?.data) {
        setIsLoading((_prev) => false);
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
        triggerRefresh && triggerRefresh();
      } else {
        setIsLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const validationSchema = Yup.object({
    name: Yup.string().required(`${t("Agency Name is required")}`),
    contact_name: Yup.string().required(`${t("Contact Name is required")}`),
    email: Yup.string().nullable().email(`${t('Please enter a valid email address')}`),
    sns_url: Yup.string().nullable().url(`${t('Please enter a valid url')}`),
    // params: Yup.array()
    //   .of(
    //     Yup.object().shape({
    //       param_key: Yup
    //         .string()
    //         .matches(/^[A-Za-z]+$/, `${t("Only allow characters, no spaces")}`),
    //       param_value: Yup.string().when('param_key', {
    //         is: (value: any) => !!value,
    //         then: (rule) => {
    //           return rule.test('param_key', `${t("This field is required")}`, (target) => !!(target?.trim()))
    //         },
    //       }),
    //     })
    //   )
    //   .unique('Key is duplicated', (a: any) => a.param_key)
    // avatar: Yup.string().required(`${t("Avatar is required")}`),
  });

  const formik = useFormik({
    initialValues: {
      avatar: "",
      name: "",
      contact_name: "",
      contact_phone: "",
      email: "",
      code: "",
      nationality: 'KR',
      sns_type: null,
      sns_url: "",
      status: 1,
      updated_at: '',
      created_at: ''
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const handleSetValueForm = (valueDefault: any) => {
    formik.setValues({
      ...valueDefault,
      updated_at: valueDefault.updated_at ? (formatDateStringToKorea(valueDefault.updated_at)) : null,
      created_at: valueDefault.created_at ? (formatDateStringToKorea(valueDefault.created_at)) : null
    });

  };

  const handleCallAgencyDetail = async (idItem: string) => {
    try {
      if (!idItem) {
        return;
      }
      const resDetail: any = await getAgencyDetail(idItem);

      if (resDetail?.data) {
        handleSetValueForm(resDetail?.data);
      }
    } catch (error: any) {
      return error;
    }
  };

  const onCloseClick = () => {
    setIsOpen(false);
    setItemToEdit(false);
  };

  useEffect(() => {
    handleCallAgencyDetail(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    document.title = `${t("Create Agency")} - ${t("Agency")} | Picaverse Agency Star+`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  useEffect(() => {
    if(Object.keys(formik.errors).length > 0) {
      setIsFirstProgress(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Object.keys(formik.errors).length])

  return (
    <React.Fragment>
      <Modal isOpen={true} centered={true} scrollable={false} size="xl">
        <ModalHeader toggle={isLoading ? () => {} : onCloseClick}>
          {isShowDetail ? t("Agency Detail") : (id ? t("Update Agency") : t("Create Agency"))}
        </ModalHeader>
        <ModalBody className="py-3 px-4">
          <div className="mb-4">
            <Progress
              value={
                ((!isFirstProgress || !!id) ? 100 - (100 / 4) * Number(Object.keys(formik.errors).length) : 0)
              }
              color="primary"
              className="animated-progress progress-sm"
            />
          </div>

          <div className="mt-3">
            <>
              {/* {isLoadingDetail && (
                <div
                  style={{
                    position: "absolute",
                    zIndex: 3,
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    backgroundColor: "rgb(164 164 164 / 36%)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Spinner size="sm" color="primary"></Spinner>
                </div>
              )} */}
              <form onSubmit={formik.handleSubmit}>
                <Row className="modal-custom-scroll" style={{overflowY: "auto", maxHeight: "68vh"}}>
                  {
                    id && <Col lg={6}>
                      <div className="mb-3">
                        <Label htmlFor="code" className="form-label">
                          {t("Agency Code")}
                        </Label>
                        <Input
                          name="code"
                          type="text"
                          className="form-control"
                          id="code"
                          disabled={true}
                          placeholder={t("Enter agency code")}
                          value={formik?.values?.code}
                          onChange={(event: any) =>
                            formik.setFieldValue("code", event?.target?.value)
                          }
                        />
                        {formik.touched.code && formik.errors.code ? (
                          <div className="text-danger mt-2">
                            {formik.errors.code}
                          </div>
                        ) : null}
                      </div>
                    </Col>
                  }
                  
                  <Col lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="agencyName" className="form-label">
                        {t("Agency Name")} <span className="text-danger">*</span>
                      </Label>
                      <Input
                        id="agencyName"
                        name="name"
                        type="text"
                        className="form-control"
                        placeholder={t("Enter agency name")}
                        value={formik?.values?.name}
                        disabled={isShowDetail}
                        onChange={(event: any) =>
                          formik.setFieldValue("name", event?.target?.value)
                        }
                      />
                      {formik.touched.name && formik.errors.name ? (
                        <div className="text-danger mt-2">
                          {formik.errors.name}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col xxl={6}>
                    <div className="mb-3">
                      <Label className="form-label">
                        {t("Nationality")}<span className="text-danger"> *</span>
                      </Label>

                      <div className="w-100">
                        <DropdownCountries
                          name="nationality"
                          dataList={[]}
                          placeholder={`${t("Select Country")}...`}
                          className="dropdown-status-rounded"
                          classNamePrefix="name-prefix"
                          disabled={isShowDetail}
                          initialValue={formik?.values?.nationality}
                          onChangeSelect={(e: any) => {
                            // console.log(e)
                            formik.setFieldValue('nationality', e?.value)
                          }}
                          isHasOptionAll={false}
                          isAllCountry={true}
                        />
                      </div>
                      
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="contactName" className="form-label">
                        {t("Contact Name")}{" "}
                        <span className="text-danger"> *</span>
                      </Label>
                      <Input
                        name="contactName"
                        type="text"
                        className="form-control"
                        id="contact_name"
                        placeholder={t("Enter contact name")}
                        value={formik?.values?.contact_name}
                        disabled={isShowDetail}
                        onChange={(event: any) =>
                          formik.setFieldValue(
                            "contact_name",
                            event?.target?.value
                          )
                        }
                      />
                      {formik.touched.contact_name &&
                      formik.errors.contact_name ? (
                        <div className="text-danger mt-2">
                          {formik.errors.contact_name}
                        </div>
                      ) : null}
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="contact_phone" className="form-label">
                        {t("Contact Phone")}
                      </Label>
                      <Input
                        name="contact_phone"
                        type="text"
                        className="form-control"
                        id="contact_phone"
                        placeholder={t("Enter contact phone number")}
                        value={formik?.values?.contact_phone}
                        disabled={isShowDetail}
                        onChange={(event: any) =>
                          formik.setFieldValue("contact_phone", event?.target?.value)
                        }
                      />
                      {formik.touched.contact_phone && formik.errors.contact_phone ? (
                        <div className="text-danger mt-2">
                          {formik.errors.contact_phone}
                        </div>
                      ) : null}
                    </div>
                  </Col>
                  <Col lg={6}>
                      <div className="mb-3">
                          <Label htmlFor="emailInput" className="form-label">{t('Email Address')}</Label>
                          <Input
                              name="email"
                              type="text"
                              className="form-control"
                              id="emailInput"
                              placeholder={t('Enter email')}
                              disabled={isShowDetail}
                              value={formik?.values?.email}
                              onChange={(event: any) => formik.setFieldValue('email', event?.target?.value)}
                          />
                          {formik.touched.email && formik.errors.email ? (
                              <div className="text-danger mt-2">{formik.errors.email}</div>
                          ) : null}
                      </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="snsTypeInput" className="form-label">{t('SNS Type')}</Label>
                      
                      <DropdownSNS
                          name="sns_type"
                          placeholder={`${t("Select SNS type")}...`}
                          className="dropdown-status-rounded"
                          classNamePrefix="name-prefix"
                          disabled={isShowDetail}
                          initialValue={formik?.values?.sns_type}
                          onChangeSelect={(e: any) => {
                            // setPlatFormSearch(e);
                            formik.setFieldValue('sns_type', e?.value)
                          }}
                          isHasOptionAll={false}
                          isClearable={true}
                        />

                      {formik.touched.sns_type && formik.errors.sns_type ? (
                          <div className="text-danger mt-2">{formik.errors.sns_type}</div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                        <Label htmlFor="urlSNSInput" className="form-label">{t('SNS URL')}</Label>
                        <Input
                            name="sns_url"
                            type="text"
                            className="form-control"
                            id="urlSNSInput"
                            placeholder={t('Enter SNS URL')}
                            disabled={isShowDetail}
                            value={formik?.values?.sns_url}
                            onChange={(event: any) => formik.setFieldValue('sns_url', event?.target?.value)}
                        />
                        {formik.touched.sns_url && formik.errors.sns_url ? (
                            <div className="text-danger mt-2">{formik.errors.sns_url}</div>
                        ) : null}
                    </div>
                  </Col>

                  {
                    id && (
                      <>
                      <Col lg={6}>
                        <div className="mb-3">
                            <Label htmlFor="created_at" className="form-label">{t('Created at')}</Label>
                            <Input
                                name="created_at"
                                type="text"
                                className="form-control"
                                id="created_at"
                                disabled={true}
                                value={formik?.values?.created_at}
                                onChange={(event: any) => {console.log(event)}}
                            />
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div className="mb-3">
                            <Label htmlFor="updatedAt" className="form-label">{t('Updated at')}</Label>
                            <Input
                                name="email"
                                type="text"
                                className="form-control"
                                id="updatedAt"
                                disabled={true}
                                value={formik?.values?.updated_at}
                                onChange={(event: any) => {console.log(event)}}
                            />
                        </div>
                      </Col>
                    </>)
                  }
                  
                  <Col lg={6}>
                    <div className="mb-3">
                      <Label className="form-label">
                        {t("Status")}
                      </Label>

                      <div>
                        <div
                          className="form-check form-switch form-switch-sm text-center"
                          dir="ltr"
                        >
                          <input
                            type="checkbox"
                            className="form-check-input"
                            name="status"
                            disabled={isShowDetail}
                            checked={formik?.values?.status === 1 ? true : false}
                            onChange={(event) => {
                              formik.setFieldValue("status", event?.target?.checked)
                            }}
                          />
                        </div>
                      </div>

                      {formik?.touched?.status && formik?.errors?.status ? (
                        <div className="text-danger mt-2">
                          {/* {formik?.errors?.status} */}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col xxl={12}>
                    <div className="mb-3">
                      <label className="form-label">
                        {t("Avatar Agency")}
                      </label>

                      <UploadImageSingle
                        data={formik.getFieldProps("avatar")?.value}
                        setLinkImage={setLinkImage}
                        formik={formik}
                        isMultiple={false}
                        uploadProps={uploadProps}
                        formikNameImage={"avatar"}
                        isDisabled={isShowDetail}
                      />
                    </div>
                  </Col>
                </Row>

                <Col lg={12}>
                  <div className="hstack gap-2 justify-content-end mt-3">
                    {!isShowDetail && (
                      <button type="submit" className="btn btn-primary fs-14">
                        {isLoading ? (
                          <Spinner size="sm" className="me-2"></Spinner>
                        ) : !!id ? (
                          <i className="ri-login-circle-line align-bottom me-2 "></i>
                        ) : (
                          <i className="ri-add-fill align-bottom me-2"></i>
                        )}
                        {id ? t("Submit Update") : t("Button Create")}
                      </button>
                    )}
                  </div>
                </Col>
              </form>
            </>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default AgencyForm;
